import { IconNames } from '@blueprintjs/icons';
import { ItemRendererProps } from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { ItemQueryList } from 'src/components/select/ItemQueryList';
import { EmptyHome } from 'src/containers/Home/EmptyHome';
import { Header } from 'src/containers/Home/Header';
import { HomeVoiceboxIntro } from 'src/containers/Home/HomeVoiceboxIntro';
import { ProjectCard } from 'src/containers/Home/ProjectCard';
import { ProjectCards } from 'src/containers/Home/ProjectCards';
import { Project } from 'src/store/state/projects';
import { ProjectSortOption } from 'src/store/ui/types';
import * as copy from 'src/templates/copy';
import { usePortalFeatures } from 'src/utils/hooks/usePortalFeatures';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

const ProjectQueryList = ItemQueryList.ofType<Project>();

const projectListPredicate = (searchText: string, project: Project) =>
  project.name.toLowerCase().includes(searchText.toLowerCase());

const PROJECTS_DEBOUNCE_THRESHOLD = 100; // arbitrarily chosen; can adjust if we encounter issues

export const Home: React.VFC = () => {
  const portalFeatures = usePortalFeatures();
  const isVoiceboxIntroVisible = portalFeatures?.data?.voiceBox;

  const projects = useReduxSelector((state) =>
    (state.projects.projectIds || [])
      .map((id) => state.projects.projectsById[id])
      .filter((project) => !project.isRemoved)
  );
  const projectSortOption = useReduxSelector(
    (state) => state.ui.projectSortOption
  );
  const sortedProjects = React.useMemo(() => {
    switch (projectSortOption) {
      case ProjectSortOption.NAME:
        return projects.sort((a, b) => a.name.localeCompare(b.name));
      case ProjectSortOption.UPDATED_AT:
        return projects.sort((a, b) => b.dateLastUpdated - a.dateLastUpdated);
      case ProjectSortOption.CREATED_AT:
        return projects.sort((a, b) => b.dateCreated - a.dateCreated);

      default:
        return projects;
    }
  }, [projectSortOption, projects]);

  return (
    <div className="sd-home sd-container">
      <ProjectQueryList
        customRenderer={({ itemList }, queryInputElement) => (
          <>
            <Header queryInputElement={queryInputElement} />
            <div
              className={classNames('sd-home__body', {
                'sd-home__body-full': !isVoiceboxIntroVisible,
              })}
            >
              {projects?.length ? itemList : <EmptyHome />}
              {isVoiceboxIntroVisible ? <HomeVoiceboxIntro /> : null}
            </div>
          </>
        )}
        debounceOnInputChange={projects.length > PROJECTS_DEBOUNCE_THRESHOLD}
        inputProps={{
          leftIcon: IconNames.SEARCH,
          placeholder: copy.components.home.search,
        }}
        itemListRenderer={ProjectCards}
        itemPredicate={projectListPredicate}
        itemRenderer={(
          project: Project,
          { modifiers, index }: ItemRendererProps
        ) => {
          return (
            <ProjectCard
              key={project.id}
              hidden={!modifiers.matchesPredicate}
              idx={index}
              project={project}
            />
          );
        }}
        items={sortedProjects}
        onSelect={noop /* TODO */}
      />
    </div>
  );
};
